import * as React from "react";
import { DensityContext } from "@salt-ds/core";

/**
 * The current density: how tall a control is and how much space sits around it.
 *
 * ── WHY THIS READS THE VENDOR'S CONTEXT OBJECT ────────────────────────────────────────────
 *
 * Density is PROVIDED, not styled. The value comes from `SaltProvider`, which this application
 * mounts inside its own `SaltFoundationProvider`, and a React context is keyed by object
 * identity — so a hook reading a context object we created would see nothing, whatever the
 * provider above it had set.
 *
 * This is the same deliberate bridge as `FormFieldContext`, and for the same reason: it lets the
 * design system be adopted one component at a time without a flag day. The HOOK is ours — its
 * name, its default, its signature — and the value comes from the provider the application
 * already mounts.
 *
 * `DEFAULT_DENSITY` is `"medium"` because that is Salt's, and a hook that silently disagreed
 * with the provider's own default would render controls at a different height from every
 * unported component beside them.
 */
/**
 * FIVE values, not four. `mobile` is easy to miss because it is not in the visual scale a
 * designer thinks in — but this application depends on it: its own density rule is desktop
 * `low`, touch `mobile`. Omitting it typechecked fine in this file and broke six call sites
 * that compare against it, which is how it was caught.
 */
export const DensityValues = ["high", "medium", "low", "touch", "mobile"] as const;

export type Density = (typeof DensityValues)[number];

export const DEFAULT_DENSITY: Density = "medium";

/**
 * @param density An explicit override. Passing one skips the context entirely, which is how a
 * component renders at a fixed density regardless of its surroundings.
 */
export function useDensity(density?: Density): Density {
  const densityFromContext = React.useContext(DensityContext) as Density | undefined;
  return density ?? densityFromContext ?? DEFAULT_DENSITY;
}
